import { useEffect, useRef, type RefObject } from "react";

import type { ClusterState } from "./api/telemetry.js";

/**
 * La geometria della testa PTZ sulla pianta, portata da `FocusMode.qml`.
 *
 * Serve a due cose che l'app desktop fa e il browser finora no: muovere il
 * fascio del laser sopra la planimetria e tenerlo fluido fra un campione di
 * telemetria e il successivo.
 */

export interface Waypoint {
  angle?: number | string;
  tilt?: number | string;
  [k: string]: unknown;
}

/**
 * Ampiezza angolare coperta dai waypoint: 360 meno il buco piu' largo.
 *
 * Serve a distinguere un **giro completo** da un **ventaglio**, e la
 * distinzione non e' estetica: nel giro la testa sta al centro della pianta e
 * il raggio ruota tutt'attorno, nel ventaglio parte dal sensore calibrato in
 * basso. Il tipo non e' salvato in tabella — `ringMode` si perde al
 * salvataggio della traccia — quindi si deduce dai waypoint stessi: un anello
 * a 12 punti copre ~330 gradi, un ventaglio da 180 ne copre ~180.
 */
export function panCoverageDeg(
  waypoints: readonly { angle?: number | string }[] | undefined,
): number {
  if (!waypoints || waypoints.length < 2) return 0;
  const a: number[] = [];
  for (const w of waypoints) {
    const v = Number(w.angle);
    if (Number.isNaN(v)) continue;
    a.push(((v % 360) + 360) % 360);
  }
  if (a.length < 2) return 0;
  a.sort((x, y) => x - y);
  let maxGap = 360 - (a[a.length - 1]! - a[0]!); // il buco che scavalca lo zero
  for (let i = 1; i < a.length; i++) {
    const g = a[i]! - a[i - 1]!;
    if (g > maxGap) maxGap = g;
  }
  return 360 - maxGap;
}

export const RING_THRESHOLD_DEG = 300;

/** Ampiezza encoder da orizzontale a fine corsa. */
const TILT_ENC_SPAN = 9000;
/** Lunghezza del raggio agli estremi del tilt, in frazione di quella a riposo:
 *  su = lontano, giu' = vicino, orizzontale a meta'. */
const TILT_FACTOR_UP = 1.0;
const TILT_FACTOR_DOWN = 0.3;

/**
 * Campione di tilt -> frazione "quanto punta in basso" [-1 su .. +1 giu'].
 *
 * L'encoder grezzo e' la fonte buona; i gradi sono il ripiego. **Sul server
 * oggi arrivano solo i gradi**: la ronda pubblica `ptzTilt`, non il conteggio
 * dell'encoder, quindi qui si passa quasi sempre dal secondo ramo. Il primo
 * resta perche' il giorno in cui la ronda pubblichera' l'encoder questo
 * codice non deve cambiare — ed e' la stessa scala dell'app desktop.
 */
export function encTiltToFrac(encTilt: number | undefined, tiltDeg: number | undefined): number {
  if (typeof encTilt === "number" && encTilt >= 0) {
    let u = encTilt;
    if (u > 18000) u -= 36000; // sgancia la cucitura 36000/0
    return Math.max(-1, Math.min(1, u / TILT_ENC_SPAN));
  }
  const d = typeof tiltDeg === "number" ? tiltDeg : 0;
  return Math.max(-1, Math.min(1, d / 90));
}

/** Frazione di tilt -> fattore di lunghezza del raggio, lineare su<->giu'. */
export function tiltFactor(frac: number): number {
  const k = (frac + 1) / 2;
  return TILT_FACTOR_UP + k * (TILT_FACTOR_DOWN - TILT_FACTOR_UP);
}

export interface BeamGeometry {
  /** Origine del fascio, normalizzata sul riquadro della pianta. */
  pivotX: number;
  pivotY: number;
  /** Punta del fascio, normalizzata. */
  endX: number;
  endY: number;
}

export interface BeamInput {
  is360: boolean;
  panDeg: number;
  tiltFrac: number;
  sensorX: number;
  sensorY: number;
  laserEnd0X: number;
  laserEnd0Y: number;
  /** Larghezza e altezza in pixel del riquadro pianta: servono a tenere
   *  circolare il giro completo su un rettangolo che circolare non e'. */
  boxW: number;
  boxH: number;
}

/** Frazione del semiasse corto usata come raggio nel giro completo. */
const RING_RADIUS = 0.4;

export function beamGeometry(i: BeamInput): BeamGeometry {
  const f = tiltFactor(i.tiltFrac);
  const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

  if (i.is360) {
    // Pan 0 = verso l'alto, come la "home" del ventaglio.
    const theta = ((i.panDeg - 90) * Math.PI) / 180;
    const short = i.boxW > 0 && i.boxH > 0 ? Math.min(i.boxW, i.boxH) : 0;
    const fx = short > 0 ? short / i.boxW : 1;
    const fy = short > 0 ? short / i.boxH : 1;
    return {
      pivotX: 0.5,
      pivotY: 0.5,
      endX: clamp01(0.5 + RING_RADIUS * f * fx * Math.cos(theta)),
      endY: clamp01(0.5 + RING_RADIUS * f * fy * Math.sin(theta)),
    };
  }

  const dx = i.laserEnd0X - i.sensorX;
  const dy = i.laserEnd0Y - i.sensorY;
  const r0 = Math.hypot(dx, dy);
  if (r0 < 0.001) {
    return { pivotX: i.sensorX, pivotY: i.sensorY, endX: i.laserEnd0X, endY: i.laserEnd0Y };
  }
  const theta = Math.atan2(dy, dx) + (i.panDeg * Math.PI) / 180;
  return {
    pivotX: i.sensorX,
    pivotY: i.sensorY,
    endX: clamp01(i.sensorX + r0 * f * Math.cos(theta)),
    endY: clamp01(i.sensorY + r0 * f * Math.sin(theta)),
  };
}

/** L'encoder del rig e' circolare su 36000. */
const ENC_FULL = 36000;
/** Punti per giro nella modalita' a fasce: uno ogni 30 gradi. */
const RING_STEPS = 12;

/** Un punto registrato, nella forma che la rotta `ptz-traces.create`
 *  pretende: `angle` e `tilt` numerici, il resto facoltativo. */
export interface RecordedWaypoint {
  label: string;
  angle: number;
  tilt: number;
  zoom: number;
  dwellSeconds: number;
  /** L'encoder grezzo, quando il rig ce l'ha. Senza, il waypoint resta in
   *  gradi e la ronda in spazio encoder lo scarta. */
  encPan?: number;
  encTilt?: number;
}

export interface RingBand {
  encTilt: number;
  tiltDeg: number;
  label: string;
}

/**
 * I waypoint di un giro a fasce, come `_generateRingWaypoints()` nel `.qml`.
 *
 * Una "fascia" e' un livello di tilt: l'operatore inclina la testa e segna,
 * senza girare in tondo dodici volte a mano. Da ogni fascia escono dodici
 * punti pan equidistanti a quel tilt, con attesa zero, concatenati fascia dopo
 * fascia — la ronda fa un giro completo, poi scende alla fascia successiva.
 *
 * Il tilt e' catturato dall'**encoder grezzo**, non convertito in gradi: la
 * conversione a 12 bit comprime la scala del tilt e scavalca la cucitura, e
 * un waypoint in gradi manderebbe la testa altrove. I gradi che escono qui
 * servono a chi legge, non alla ronda.
 */
export function generateRingWaypoints(
  bands: RingBand[],
  startEncPan: number,
  zoom: number,
): RecordedWaypoint[] {
  const panStep = ENC_FULL / RING_STEPS;
  const start = startEncPan >= 0 ? startEncPan : 0;
  const out: RecordedWaypoint[] = [];
  for (const band of bands) {
    for (let i = 0; i < RING_STEPS; i++) {
      const encPan = Math.round(start + i * panStep) % ENC_FULL;
      out.push({
        label: `${band.label}·${(i * 360) / RING_STEPS}°`,
        angle: Math.round(((encPan * 360) / ENC_FULL) * 10) / 10,
        tilt: Math.round(band.tiltDeg * 10) / 10,
        zoom,
        dwellSeconds: 0,
        encPan,
        encTilt: band.encTilt,
      });
    }
  }
  return out;
}

export interface SmoothPtz {
  angle: number;
  tilt: number;
  zoom: number;
  tiltFrac: number;
}

const LERP_MIN_MS = 200;
const LERP_MAX_MS = 1200;

/**
 * Posizione della testa interpolata a 60 fps.
 *
 * La telemetria arriva a passi discreti; disegnare il fascio direttamente su
 * quei valori lo fa saltare da un waypoint all'altro. Due dettagli che nel
 * `.qml` sono commentati e vanno tenuti:
 *
 *  - **la durata dell'interpolazione insegue la cadenza vera** dei campioni
 *    (media mobile). Fissata a un secondo, la pianta mostrerebbe sempre la
 *    posizione di un secondo fa.
 *  - **il pan prende la strada corta**. Il server manda l'angolo avvolto in
 *    [-180,+180]: a cavallo della cucitura, da +179 a -179, un'interpolazione
 *    lineare farebbe girare il raggio all'indietro di quasi un giro intero.
 *
 * Restituisce un **riferimento**, non uno stato: chi disegna il fascio ha gia'
 * il suo ciclo a 60 fps e legge di li'. Passando da `useState` questa
 * schermata — video, tabella eventi, colonna PTZ — si ridisegnerebbe sessanta
 * volte al secondo per muovere una linea dentro una tela.
 */
export function usePtzSmooth(telemetry: ClusterState | undefined): RefObject<SmoothPtz> {
  const from = useRef<SmoothPtz>({ angle: 0, tilt: 0, zoom: 30, tiltFrac: 0 });
  const to = useRef<SmoothPtz>({ angle: 0, tilt: 0, zoom: 30, tiltFrac: 0 });
  const current = useRef<SmoothPtz>({ angle: 0, tilt: 0, zoom: 30, tiltFrac: 0 });
  const startMs = useRef(0);
  const durationMs = useRef(500);
  const lastSampleMs = useRef(0);

  const angle = telemetry?.ptzAngle;
  const tilt = telemetry?.ptzTilt;
  const zoom = telemetry?.ptzZoom;
  const encTilt = (telemetry as { ptzEncTilt?: number } | undefined)?.ptzEncTilt;

  useEffect(() => {
    let target = typeof angle === "number" ? angle : current.current.angle;
    let d = target - current.current.angle;
    while (d > 180) {
      target -= 360;
      d -= 360;
    }
    while (d < -180) {
      target += 360;
      d += 360;
    }
    from.current = { ...current.current };
    to.current = {
      angle: target,
      tilt: typeof tilt === "number" ? tilt : current.current.tilt,
      zoom: typeof zoom === "number" ? zoom : current.current.zoom,
      tiltFrac: encTiltToFrac(encTilt, tilt),
    };
    const now = performance.now();
    if (lastSampleMs.current > 0) {
      const dt = now - lastSampleMs.current;
      durationMs.current = Math.max(
        LERP_MIN_MS,
        Math.min(LERP_MAX_MS, durationMs.current * 0.7 + dt * 0.3),
      );
    }
    lastSampleMs.current = now;
    startMs.current = now;
  }, [angle, tilt, zoom, encTilt]);

  useEffect(() => {
    let raf = 0;
    const frame = () => {
      const raw = Math.min((performance.now() - startMs.current) / durationMs.current, 1);
      const k = raw * raw * (3 - 2 * raw); // smoothstep
      const f = from.current;
      const t = to.current;
      current.current = {
        angle: f.angle + (t.angle - f.angle) * k,
        tilt: f.tilt + (t.tilt - f.tilt) * k,
        zoom: f.zoom + (t.zoom - f.zoom) * k,
        tiltFrac: f.tiltFrac + (t.tiltFrac - f.tiltFrac) * k,
      };
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, []);

  return current;
}
